import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { CartItem, ProductType, ProductOption } from "../shop/productTypes";
import { calculatePhotoEnhancementPricing } from "../../utils/generationCalculations";

type ShoppingCartContextType = {
  cart: CartItem[];
  addToCart: (
    product: ProductType,
    quantity: number,
    selectedOptions: { [key: string]: any }
  ) => void;
  removeFromCart: (cartItemId: string) => void;
  updateQuantity: (cartItemId: string, quantity: number) => void;
  clearCart: () => void;
};

const ShoppingCartContext = createContext<ShoppingCartContextType | undefined>(
  undefined
);

type ShoppingCartProviderProps = {
  children: ReactNode;
};

export const ShoppingCartProvider: React.FC<ShoppingCartProviderProps> = ({
  children,
}) => {
  const userId = auth.currentUser?.uid;
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const fetchCart = async () => {
      if (userId && !isInitialized) {
        try {
          const userDocRef = doc(db, "users", userId);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const cartData = userDoc.data()?.shoppingCart || [];

            // Migrate existing cart items that don't have unique IDs
            const migratedCartData = cartData.map((item: any) => {
              if (!item.id) {
                return {
                  ...item,
                  id: `${item.productId}-${Date.now()}-${Math.random()
                    .toString(36)
                    .substr(2, 9)}`,
                };
              }
              return item;
            });

            setCart(migratedCartData);

            // Update Firestore with migrated data if needed
            if (
              migratedCartData.some(
                (item: any, index: number) => !cartData[index]?.id
              )
            ) {
              updateCartInFirestore(migratedCartData);
            }
          }
        } catch (error) {
          console.error("Error fetching cart:", error);
          setCart([]);
        }
        setIsInitialized(true);
      }
    };

    fetchCart();
  }, [userId, isInitialized]);

  const updateCartInFirestore = async (updatedCart: CartItem[]) => {
    if (userId) {
      try {
        const userDocRef = doc(db, "users", userId);
        await setDoc(
          userDocRef,
          { shoppingCart: updatedCart },
          { merge: true }
        );
        console.log(
          "Cart updated successfully with",
          updatedCart.length,
          "items"
        );
      } catch (error) {
        console.error("Error updating cart in Firestore:", error);
      }
    }
  };

  const getOptionSummary = (
    options: ProductOption[],
    selectedOptions: { [key: string]: any }
  ) => {
    return options
      .map((option) => {
        const selectedValue = selectedOptions[option.id];
        if (option.type === "select") {
          const selectedOption = option.values.find(
            (value) => value.id === selectedValue
          );
          return selectedOption
            ? `${option.label}: ${selectedOption.label}`
            : `${option.label}: undecided`;
        }
        if (option.type === "number") {
          return `${option.label}: ${selectedValue}`;
        }
        if (option.type === "boolean") {
          return `${option.label}: ${selectedValue ? "Yes" : "No"}`;
        }
        return "";
      })
      .join("\n");
  };

  const addToCart = (
    product: ProductType,
    quantity: number,
    selectedOptions: { [key: string]: any }
  ) => {
    // Calculate the actual price based on selected options
    let actualPrice = product.price;
    product.options?.forEach((option) => {
      const selectedValue = selectedOptions[option.id];
      if (option.type === "select") {
        const selectedOption = option.values.find(
          (v) => v.id === selectedValue
        );
        if (selectedOption) {
          actualPrice += selectedOption.cost;
        }
      } else if (option.type === "number") {
        const extraValue = selectedValue - option.min;
        if (extraValue > 0) {
          actualPrice += extraValue * option.cost;
        }
      } else if (option.type === "boolean" && selectedValue) {
        // Handle dynamic pricing for photo enhancement
        if (
          option.cost === "dynamic" &&
          option.dynamicPricing?.type === "photoEnhancement"
        ) {
          const pricing = calculatePhotoEnhancementPricing(
            product.id,
            selectedOptions
          );
          actualPrice += pricing.bulkPrice;
        } else if (typeof option.cost === "number") {
          actualPrice += option.cost;
        }
      }
    });

    // Generate the description and image based on selected options
    const description = getOptionSummary(
      product.options || [],
      selectedOptions
    );
    const image = product.images[0]; // You can choose to select based on options if needed

    const cartItem: CartItem = {
      id: `${product.id}-${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 9)}`, // Generate unique ID
      productId: product.id,
      name: product.title,
      price: actualPrice, // Use calculated price instead of base price
      quantity,
      selectedOptions,
      description,
      image,
      requiredForms: product.requiredForms,
    };

    let updatedCart;

    // if (existingItemIndex !== -1) {
    //   updatedCart = cart.map((cartItem, index) =>
    //     index === existingItemIndex
    //       ? { ...cartItem, quantity: cartItem.quantity + quantity }
    //       : cartItem
    //   );
    // } else {
    updatedCart = [...cart, cartItem];
    // }

    setCart(updatedCart);
    updateCartInFirestore(updatedCart);
  };

  const removeFromCart = useCallback(
    (cartItemId: string) => {
      setCart((prevCart) => {
        const updatedCart = prevCart.filter((item) => item.id !== cartItemId);
        updateCartInFirestore(updatedCart);
        return updatedCart;
      });
    },
    [userId]
  );

  const updateQuantity = useCallback(
    (cartItemId: string, quantity: number) => {
      setCart((prevCart) => {
        const updatedCart = prevCart.map((item) =>
          item.id === cartItemId ? { ...item, quantity } : item
        );
        updateCartInFirestore(updatedCart);
        return updatedCart;
      });
    },
    [userId]
  );

  const clearCart = useCallback(() => {
    setCart([]);
    updateCartInFirestore([]);
  }, [userId]); // Only depends on userId

  return (
    <ShoppingCartContext.Provider
      value={{ cart, addToCart, removeFromCart, updateQuantity, clearCart }}
    >
      {children}
    </ShoppingCartContext.Provider>
  );
};

export const useShoppingCart = (): ShoppingCartContextType => {
  const context = useContext(ShoppingCartContext);
  if (!context) {
    throw new Error(
      "useShoppingCart must be used within a ShoppingCartProvider"
    );
  }
  return context;
};
